import React, { useEffect, useState } from "react";
import { api, formatApiError, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, Shield, X } from "lucide-react";

const TYPE_LABEL = {
  void: "Void", discount: "Discount", comp: "Comp", refund: "Refund",
  price_override: "Price override", manual: "Manual",
};
const KIND_LABEL = {
  "pos.void_item":      "POS · Void line",
  "pos.discount":       "POS · Discount",
  "inventory.movement": "Inventory · Movement",
  "event.ticket_void":  "Event · Void ticket",
};
const STATUS_COLORS = {
  pending:   "text-amber-500 border-amber-500/40",
  approved:  "text-emerald-500 border-emerald-500/40",
  denied:    "text-red-500 border-red-500/40",
  cancelled: "text-foreground/40 border-foreground/15",
  expired:   "text-foreground/40 border-foreground/15",
};

export default function ApprovalsPage() {
  const { branchId } = useAuth();
  const [list, setList] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("pending");
  const [acting, setActing] = useState(null); // { approval, mode: "approve"|"deny", reason }

  const load = async () => {
    try {
      const params = { limit: 100 };
      if (branchId) params.branch_id = branchId;
      if (filter !== "all") params.status = filter;
      const [{ data }, { data: st }] = await Promise.all([
        api.get("/approvals", { params }),
        api.get("/approvals/stats", { params: branchId ? { branch_id: branchId } : {} }),
      ]);
      setList(data); setStats(st);
    } catch (_) {}
  };
  useEffect(() => { load(); const iv = setInterval(load, 8000); return () => clearInterval(iv); }, [branchId, filter]);

  const decide = async () => {
    if (!acting) return;
    if (acting.mode === "deny" && !acting.reason.trim()) { toast.error("Denial reason required"); return; }
    try {
      await api.post(`/approvals/${acting.approval.id}/${acting.mode}`, { reason: acting.reason });
      toast.success(`Request ${acting.mode === "approve" ? "approved" : "denied"}`);
      setActing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="approvals-page">
      <div className="flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="section-eyebrow ink-subtle">Approvals · Central engine</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">Manager desk</h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">
            Every sensitive request — voids, discounts, comps, refunds, price overrides — funnels here for a manager's yes or no. Every decision is audit-logged with a reason.
          </p>
        </div>
        <div className="text-right">
          <div className="section-eyebrow">Pending review</div>
          <div className="mt-3 display-heading text-6xl mono text-amber-500" data-testid="app-pending-count">
            {stats.pending || 0}
          </div>
        </div>
      </div>

      <div className="mt-14 grid grid-cols-2 md:grid-cols-5 gap-x-8 gap-y-6 animate-fade-up animate-fade-up-1">
        {["pending","approved","denied","cancelled","expired"].map((s, i) => (
          <div key={s} data-testid={`app-stat-${s}`}>
            <div className="flex items-center justify-between">
              <div className="section-eyebrow">{s}</div>
              <span className="section-num">.0{i+1}</span>
            </div>
            <div className={`mt-3 mono text-2xl font-semibold ${s === "pending" ? "text-amber-500" : s === "denied" ? "text-red-500" : "ink"}`}>{stats[s] || 0}</div>
          </div>
        ))}
      </div>

      <div className="mt-10 flex items-center gap-2 animate-fade-up animate-fade-up-2">
        {["all","pending","approved","denied","cancelled"].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
                  data-testid={`app-filter-${f}`}
                  className={`h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.24em] font-bold border transition-colors ${
                    filter === f ? "bg-foreground text-background border-foreground" : "hairline text-foreground/60 hover:text-foreground"
                  }`}>{f}</button>
        ))}
      </div>

      <div className="mt-8 border-y hairline-soft divide-y hairline-soft animate-fade-up animate-fade-up-3" data-testid="app-table">
        <div className="grid grid-cols-12 py-3 tiny-label ink-subtle">
          <div className="col-span-2">Requested</div>
          <div className="col-span-1">Type</div>
          <div className="col-span-2">Requestor</div>
          <div className="col-span-3">Reason</div>
          <div className="col-span-1 text-right">Amount</div>
          <div className="col-span-1 text-right">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {list.length === 0 && <div className="py-8 ink-subtle text-center text-sm">No approvals in this bucket.</div>}
        {list.map((a) => (
          <div key={a.id} className="grid grid-cols-12 py-3 items-center" data-testid={`app-row-${a.id}`}>
            <div className="col-span-2 mono text-[11px] ink-subtle">
              {new Date(a.requested_at).toLocaleString("en-PH", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })}
            </div>
            <div className="col-span-1 tiny-label">{TYPE_LABEL[a.type] || a.type}</div>
            <div className="col-span-2 text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{a.requested_by_name}</div>
            <div className="col-span-3 text-xs ink truncate pr-2">
              {a.reason}
              {a.ref_label && <span className="ink-subtle italic block mt-0.5 text-[10px]">{a.ref_label}</span>}
              {a.payload?.kind && (
                <span className="mt-1 inline-block text-[8px] uppercase tracking-[0.22em] font-bold px-1.5 py-0.5 rounded-full bg-indigo-500/10 text-indigo-500 border border-indigo-500/30" data-testid={`app-kind-${a.id}`}>
                  {KIND_LABEL[a.payload.kind] || a.payload.kind}
                </span>
              )}
            </div>
            <div className="col-span-1 text-right mono text-sm ink">{a.amount != null ? peso(a.amount) : "—"}</div>
            <div className="col-span-1 text-right">
              <span className={`text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded-full border ${STATUS_COLORS[a.status]}`}>
                {a.status}
              </span>
            </div>
            <div className="col-span-2 text-right flex justify-end gap-1">
              {a.status === "pending" ? (
                <>
                  <button onClick={() => setActing({ approval: a, mode: "approve", reason: "" })}
                          data-testid={`app-approve-${a.id}`}
                          className="h-8 px-3 rounded-full border border-emerald-500/40 text-emerald-500 text-[10px] uppercase tracking-[0.22em] font-bold hover:bg-emerald-500/5">
                    <CheckCircle2 size={11} className="inline -mt-0.5"/> Approve
                  </button>
                  <button onClick={() => setActing({ approval: a, mode: "deny", reason: "" })}
                          data-testid={`app-deny-${a.id}`}
                          className="h-8 px-3 rounded-full border border-red-500/40 text-red-500 text-[10px] uppercase tracking-[0.22em] font-bold hover:bg-red-500/5">
                    <XCircle size={11} className="inline -mt-0.5"/> Deny
                  </button>
                </>
              ) : (
                <div className="tiny-label ink-subtle italic truncate">
                  {a.reviewed_by_name ? `${a.reviewed_by_name}${a.review_reason ? ` · ${a.review_reason}` : ""}` : "—"}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {acting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="app-decision-dialog">
          <div className="absolute inset-0 bg-black/70" onClick={() => setActing(null)}/>
          <div className="relative w-full max-w-md surface-2 border hairline rounded-lg p-6 shadow-2xl">
            <div className="section-eyebrow">{acting.mode === "approve" ? "Approve request" : "Deny request"}</div>
            <h2 className="mt-2 display-heading text-2xl ink">
              {TYPE_LABEL[acting.approval.type]}{acting.approval.amount != null ? ` · ${peso(acting.approval.amount)}` : ""}
            </h2>
            <p className="mt-3 ink-muted text-xs italic">"{acting.approval.reason}"</p>
            {acting.approval.payload?.kind && (
              <div className="mt-3 rounded-md hairline border p-3 text-[10px] uppercase tracking-[0.22em] font-bold text-indigo-500 flex items-center gap-2" data-testid="app-decision-kind">
                <Shield size={12}/> Auto-executes: {KIND_LABEL[acting.approval.payload.kind] || acting.approval.payload.kind}
              </div>
            )}
            <label className="block mt-6">
              <div className="tiny-label">{acting.mode === "approve" ? "Note (optional)" : "Reason (required)"}</div>
              <textarea rows={3} autoFocus value={acting.reason}
                        onChange={(e) => setActing({ ...acting, reason: e.target.value })}
                        data-testid="app-decision-reason"
                        className="mt-2 w-full rounded-md border hairline surface-card p-3 ink text-sm"/>
            </label>
            <div className="mt-6 flex gap-2">
              <button onClick={() => setActing(null)} className="btn-pill btn-pill-outline h-11 flex-1">Cancel</button>
              <button onClick={decide}
                      data-testid="app-decision-confirm"
                      disabled={acting.mode === "deny" && !acting.reason.trim()}
                      className="btn-pill btn-pill-solid h-11 flex-1 disabled:opacity-50">
                {acting.mode === "approve" ? "Approve" : "Deny"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
